import Button from "@mui/material/Button";
import {Alert, Box, CircularProgress, FormControl, Grid, MenuItem, Select, Typography} from "@mui/material";
import React, {useState} from "react";
import {LoadingButton} from "@mui/lab";
import {useMutation} from "react-query";
import {useTranslation} from "react-i18next";
import {LifecycleMail, NewsletterMailStatus} from "crqlar-react-common/dist/models/newsletter/newsletterMail";
import {CreateMailResponse} from "crqlar-react-common/dist/models/newsletter/createMailResponse";
import {DaysBeforeSend, LifeCycleMailType} from "crqlar-react-common/dist/models/newsletter/newsletterMailType";
import {Datepicker} from "@mobiscroll/react";
import {useNewsletterAccountQuery} from "crqlar-react-common/dist/utils/hooks/newsletter/useNewsletterAccount";
import {ApiResponse} from "crqlar-react-common/dist/models/newsletter/apiResponse.model";
import {
    NewsletterLifecycleMailService
} from "crqlar-react-common/dist/services/newsletter/newsletterLifecycleMail.service";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

interface Props {
    handleClose: () => void
    activeStep: number
    lifecycleMail?: LifecycleMail
    setLifecycleMail: (lifecycleMail: LifecycleMail) => void
    daysBeforeSend: string[]
    onCloseDialog: () => void
    showSnackbar: (message: string, severity: "success" | "error" | "info" | "warning") => void
}

const Schedule = (props: Props) => {

    const readOnlyUser = useReadOnly()

    const [createMailResponse, setCreateMailResponse] = useState<CreateMailResponse>({
        content: '',
        statusCode: undefined
    });
    const [dailyExecutionTime, setDailyExecutionTime] = useState<string | null>('12:00');
    const {t} = useTranslation()

    const saveLifecycleMail = useMutation(
        'saveLifecycleMail',
        async () => {
            await handleCreateOrUpdateLifecycleMail()
        }
    )

    const newsletterAccountQ = useNewsletterAccountQuery();
    const newsletterAccountAccess = newsletterAccountQ.data;

    const handleTimeChange = (newValue: string | null) => {
        setDailyExecutionTime(newValue);
        props.setLifecycleMail({
            ...props.lifecycleMail,
            dailyExecutionTime: newValue
        })
    }

    const handleStatusChange = (newStatus: NewsletterMailStatus) => {
        if (newStatus !== null) {
            props.setLifecycleMail({
                ...props.lifecycleMail,
                status: newStatus
            })
        }
    }

    const handleCreateOrUpdateLifecycleMail = async () => {
        const isNew = !props.lifecycleMail?.id;
        if (!props.lifecycleMail) return

        try {
            let response: ApiResponse<LifecycleMail>
            if (isNew) {
                response = await NewsletterLifecycleMailService.createLifecycleMail(props.lifecycleMail)
            } else {
                response = await NewsletterLifecycleMailService.updateLifecycleMail(props.lifecycleMail)
            }

            // Only if successfully creating new lifecycle mail, we open the new created mailing in Maileon
            if (isNew && response.severity === 'success') {
                window.open(
                    `${newsletterAccountAccess?.newsletterAccountLoginUrl}/eagle_kp_webapp/mln/campaigns/triggers?eId=${response.data?.maileonMailingId}`,
                    '_blank' // Opens the link in a new tab
                )
            }
            props.showSnackbar(t(response.message), response.severity)
            if(response.severity === 'success') {
                props.onCloseDialog()
            }
        } catch (err: any) {
            props.showSnackbar(t(err.response.data.message), 'error')
        }
    }

    const isButtonDisabled = () => {
        if(saveLifecycleMail.isLoading) return true
        if(!props.lifecycleMail?.name) return true
        if(!props.lifecycleMail.type) return true
        if(!props.lifecycleMail.daysBeforeSend) return true

        // Maileon rule (sealed newsletter) - If status is not DRAFT - it cannot be updated
        if((props.lifecycleMail.id && props.lifecycleMail.status !== NewsletterMailStatus.DRAFT)) return true
        return false
    }

    const renderNonDraftWarning = () => {
        if(props.lifecycleMail?.status === NewsletterMailStatus.DRAFT) return
        // Warning should appear only if lifecycleMail is already created
        if(!props.lifecycleMail?.id) return

        return(
            <Box display="flex" justifyContent="center" sx={{mb: 4}}>
                <Alert severity={'warning'}>
                    <Typography variant={'h6'}>
                        {t(`Only draft lifecycle mails can be modified`)}
                    </Typography>
                </Alert>
            </Box>
        )
    }

    const renderScheduleInfoAlert = () => {
        return (
            <Box display="flex" justifyContent="center" sx={{mb: 4}}>
                <Alert severity={'info'}>
                    <Typography variant={'h6'}>
                        {t(`The first mailing will be sent starting from tomorrow at scheduled time.`)}
                    </Typography>
                </Alert>
            </Box>
        )
    }

    return (
        <Box>
            <Grid container spacing={4} sx={{mb: 4}}>
                <Grid item md={6}>
                    <FormControl fullWidth>
                        {
                            props.lifecycleMail?.daysBeforeSend === DaysBeforeSend.TODAY &&
                            props.lifecycleMail?.subType !== LifeCycleMailType.BIRTHDAY_EMAIL &&
                            <Typography>{t('Send Today')}</Typography>
                        }
                        {
                            props.lifecycleMail?.daysBeforeSend === DaysBeforeSend.TODAY &&
                            props.lifecycleMail?.subType === LifeCycleMailType.BIRTHDAY_EMAIL &&
                            <Typography>{t('Send on day of the birthday')}</Typography>
                        }

                        {props.lifecycleMail?.daysBeforeSend !== DaysBeforeSend.TODAY &&
                            (props.lifecycleMail?.subType === LifeCycleMailType.PRE_ARRIVAL_EMAIL ||
                                props.lifecycleMail?.subType === LifeCycleMailType.BIRTHDAY_EMAIL
                            ) &&
                            <Typography>{t('Send X Days Before', {days: props.lifecycleMail?.daysBeforeSend})}</Typography>
                        }
                        {
                            props.lifecycleMail?.daysBeforeSend !== DaysBeforeSend.TODAY &&
                            (props.lifecycleMail?.subType === LifeCycleMailType.POST_STAY_EMAIL ||
                                props.lifecycleMail?.subType === LifeCycleMailType.OFFER_EMAIL
                            ) &&
                            <Typography>{t('Send After X Days', {days: props.lifecycleMail.daysBeforeSend})}</Typography>
                        }
                        <Select
                            value={props.lifecycleMail?.daysBeforeSend || props.daysBeforeSend?.[0]}
                            onChange={(e) =>
                                props.setLifecycleMail({
                                    ...props.lifecycleMail,
                                    daysBeforeSend: e.target.value
                                })
                            }
                        >
                            {props.daysBeforeSend.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option === "0"
                                        ? (props.lifecycleMail?.subType !== LifeCycleMailType.BIRTHDAY_EMAIL
                                            ? t("Today")
                                            : t("On day of the birthday"))
                                        : option}
                                </MenuItem>
                            ))}
                            {/* Add more options as needed */}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item md={6}>
                    <Typography sx={{textAlign: 'center', lineHeight: '1.5', mt: 2}}>
                        {t('Email trigger days before message')}
                    </Typography>
                </Grid>

                <Grid item md={12} sx={{display: 'flex', justifyContent: 'center'}}>

                    <Datepicker
                        value={props.lifecycleMail?.dailyExecutionTime}
                        onChange={(event: any) => {
                            const date = event.value
                            // Generate HH:mm out of Date, make sure it is always 2 digits
                            const hours = date.getHours();
                            const minutes = date.getMinutes();
                            const hoursString = hours < 10 ? `0${hours}` : `${hours}`;
                            const minutesString = minutes < 10 ? `0${minutes}` : `${minutes}`;
                            const time = `${hoursString}:${minutesString}`;

                            props.setLifecycleMail({
                                ...props.lifecycleMail,
                                dailyExecutionTime: time
                            })
                        }}
                        theme={'material'}
                        themeVariant="light"
                        timeFormat={'HH:mm'}
                        labelStyle={'floating'}
                        inputStyle="outline"
                        // placeholder={props.placeholder || t("Please Select...")}
                        responsive={{
                            xsmall: {
                                controls: ['time'],
                                display: 'bottom',
                                touchUi: true
                            },
                            small: {
                                controls: ['time'],
                                display: 'anchored',
                                touchUi: true
                            },
                            custom: { // Custom breakpoint
                                breakpoint: 800,
                                controls: ['time'],
                                display: 'anchored',
                                touchUi: false
                            }
                        }}
                        firstDay={1}
                    />

                </Grid>

            </Grid>

            {renderScheduleInfoAlert()}

            {renderNonDraftWarning()}

            <Box display="flex" justifyContent="center" sx={{mb: 4}}>
                {
                    createMailResponse.content && createMailResponse.statusCode === 200 &&
                    <Alert>
                        <Typography variant={'h6'}>
                            {createMailResponse.content}
                        </Typography>
                    </Alert>
                }

                {
                    createMailResponse.content && createMailResponse.statusCode !== 200 &&
                    <Alert severity="error">
                        <Typography variant={'h6'}>
                            {createMailResponse.content}
                        </Typography>
                    </Alert>
                }

                {
                    saveLifecycleMail.isLoading &&
                    <Box textAlign={"center"}>
                        <CircularProgress size={40}></CircularProgress>
                    </Box>
                }
            </Box>

            <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                <Button
                    variant="text"
                    onClick={props.handleClose}
                >
                    {t('Close')}
                </Button>
                <LoadingButton
                    disabled={
                        isButtonDisabled() || readOnlyUser
                    }
                    variant="contained"
                    onClick={() => {
                        saveLifecycleMail.mutate()
                    }}
                    loading={saveLifecycleMail.isLoading}
                >
                    {props.lifecycleMail?.id ? t('Update') : t('Create Lifecycle Mail')}
                </LoadingButton>
            </Box>
        </Box>
    )
}

export default Schedule
